import { FormProvider, getFormProps } from '@conform-to/react'
import { IconButton, Tooltip } from '@radix-ui/themes'
import { animated, useSpring } from '@react-spring/web'
import { useLocation } from '@remix-run/react'
import * as React from 'react'
import { RxHeart, RxHeartFilled } from 'react-icons/rx'
import { useHydrated } from 'remix-utils/use-hydrated'
import { z } from 'zod'
import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '#app/components/common/form-defaults.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type Locale } from '#app/models/locale.ts'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	type FormEnum,
} from '#app/routes/_layout/route-config.ts'
import { generateItemFormFetcherKey } from '#app/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '#app/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useAddToWishlistForm } from '#app/utils/hooks/forms/use-add-to-wishlist-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Add to wishlist form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	productId = 'product-id',
	productVariantId = 'product-variant-id',
}

const FORM: FormEnum = {
	generateId: ({ id }) => generateItemFormId('add-to-wishlist', id),
	action: '/profile/wishlist',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('add-to-wishlist', id),
	subaction: 'add-to-wishlist',
	redirect: '/profile/wishlist',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.productId]: z.number(),
	},
})

type AddToWishlistFormProps = {
	product: GetProduct
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
	loggedIn: boolean
}

const AddToWishlistForm: React.FC<AddToWishlistFormProps> = ({
	product,
	locale,
	setApp,
	loggedIn,
}) => {
	const {
		addToWishlistFetcher,
		addToWishlistFormValues,
		addToWishlistForm,
		addToWishlistFields,
		t,
	} = useAddToWishlistForm({ product })

	const location = useLocation()
	const isHydrated = useHydrated()

	const [hover, setHover] = React.useState(false)
	const [springState, toggle] = React.useState(true)

	const { x } = useSpring({
		from: { x: 1 },
		x: springState ? 1 : 0,
		config: { duration: 1000 },
	})

	return (
		<FormProvider context={addToWishlistForm.context}>
			<addToWishlistFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}${location.search}`}
				{...getFormProps(addToWishlistForm)}
				preventScrollReset
			>
				<FormDefaults
					form={addToWishlistForm}
					fields={addToWishlistFields}
					values={addToWishlistFormValues}
				/>
				<FormDefaultsHiddenField meta={addToWishlistFields[INPUTS.productId]} />
				<animated.div
					style={{
						scale: x.to({
							range: [0, 0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 1],
							output: [1, 0.87, 0.8, 1.2, 0.6, 1.2, 1.13, 1],
						}),
					}}
				>
					<Tooltip
						content={
							loggedIn
								? t('button.addToWishlist')
								: t('button.pleaseLogInToUseWishlistFeature')
						}
					>
						<IconButton
							className="app-WishlistButton m-2"
							size="3"
							variant="ghost"
							type={loggedIn ? 'submit' : 'button'}
							onMouseEnter={() => setHover(true)}
							onFocus={() => setHover(true)}
							onMouseLeave={() => setHover(false)}
							onBlur={() => setHover(false)}
							onClick={() => {
								if (!loggedIn) {
									setApp((prev) => ({
										...prev,
										modalView: ROOT_APP_VIEW.loginView,
										displayModal: true,
										options: {
											...prev.options,
											redirect: location.pathname,
											noJs: String(!isHydrated),
										},
									}))
								} else {
									toggle((prev) => !prev)
									setHover(false)
								}
							}}
						>
							{loggedIn && hover ? (
								<RxHeartFilled size="1.4em" />
							) : (
								<RxHeart size="1.4em" />
							)}
						</IconButton>
					</Tooltip>
				</animated.div>
			</addToWishlistFetcher.Form>
		</FormProvider>
	)
}
AddToWishlistForm.displayName = 'AddToWishlistForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type AddToWishlistFormProps,
	AddToWishlistForm,
	INPUTS as ADD_TO_WISHLIST_INPUTS,
	FORM as ADD_TO_WISHLIST_FORM,
	schema as addToWishlistSchema,
}
